import { useState } from "react";
import { MagnifyingGlass, Package, MapPin, CheckCircle, Clock, Bluetooth, TruckTrailer, ArrowRight } from "@phosphor-icons/react";
import axios from "axios";
import ShipmentChat from "@/components/ShipmentChat";
import RealtimeEtaCard from "@/components/RealtimeEtaCard";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_LABELS = {
  created: "Criada", bag_assigned: "Bolsa atribuida", awaiting_pickup: "A aguardar recolha",
  picked_up: "Recolhida", in_transit: "Em transito", at_delivery_point: "No ponto de entrega",
  delivered: "Entregue", awaiting_payment: "A aguardar pagamento", paid: "Pago",
};

const STATUS_COLORS = {
  created: { bg: "rgba(92,92,92,0.1)", color: "var(--text-muted)" },
  bag_assigned: { bg: "rgba(45,74,62,0.1)", color: "var(--primary)" },
  awaiting_pickup: { bg: "rgba(217,108,74,0.1)", color: "var(--accent)" },
  picked_up: { bg: "rgba(37,99,235,0.1)", color: "#2563eb" },
  in_transit: { bg: "rgba(37,99,235,0.1)", color: "#2563eb" },
  at_delivery_point: { bg: "rgba(232,163,89,0.1)", color: "var(--warning)" },
  delivered: { bg: "rgba(62,102,86,0.1)", color: "var(--success)" },
  awaiting_payment: { bg: "rgba(232,163,89,0.1)", color: "var(--warning)" },
  paid: { bg: "rgba(62,102,86,0.1)", color: "var(--success)" },
};

export default function PublicTracking() {
  const [code, setCode] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleTrack = async (e) => {
    e?.preventDefault();
    if (!code.trim()) return;
    setLoading(true);
    setError("");
    setResult(null);
    try {
      const res = await axios.get(`${API}/track/${code.trim().toUpperCase()}`);
      setResult(res.data);
    } catch (err) {
      setError(err.response?.status === 404 ? "Encomenda nao encontrada. Verifique o codigo." : "Erro ao procurar encomenda.");
    } finally {
      setLoading(false);
    }
  };

  const sc = result ? (STATUS_COLORS[result.status] || STATUS_COLORS.created) : null;

  return (
    <div data-testid="public-tracking" className="pt-20 px-6 pb-12">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-10 animate-fade-in-up">
          <div style={{ width: 72, height: 72, borderRadius: 20, background: "rgba(37,99,235,0.08)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
            <MagnifyingGlass size={36} weight="duotone" style={{ color: "#2563eb" }} />
          </div>
          <h1 style={{ fontFamily: "Outfit", fontWeight: 700, fontSize: "1.8rem", marginBottom: 6 }}>Rastrear encomenda</h1>
          <p style={{ color: "var(--text-muted)", fontSize: "0.95rem" }}>Insira o codigo de rastreio para acompanhar a sua encomenda</p>
        </div>

        <form onSubmit={handleTrack} className="flex gap-3 mb-8 animate-fade-in-up stagger-1" data-testid="tracking-form">
          <input
            data-testid="tracking-input"
            value={code}
            onChange={e => setCode(e.target.value)}
            placeholder="Ex: BM-2026-00147"
            style={{ flex: 1, padding: "14px 18px", borderRadius: 14, border: "1px solid var(--border-color)", fontFamily: "JetBrains Mono", fontSize: "1rem", background: "var(--surface)", outline: "none", letterSpacing: "0.03em" }}
          />
          <button
            data-testid="tracking-submit-btn"
            type="submit"
            disabled={loading || !code.trim()}
            style={{ padding: "14px 28px", borderRadius: 14, border: "none", background: "var(--primary)", color: "white", fontFamily: "Outfit", fontWeight: 600, fontSize: "0.95rem", cursor: "pointer", display: "flex", alignItems: "center", gap: 8, opacity: loading ? 0.6 : 1 }}
          >
            {loading ? "A procurar..." : "Rastrear"} <ArrowRight size={18} weight="bold" />
          </button>
        </form>

        {error && (
          <div data-testid="tracking-error" className="bento-item p-6 text-center animate-fade-in" style={{ borderColor: "rgba(184,74,74,0.2)" }}>
            <Package size={40} weight="duotone" style={{ color: "var(--error)", margin: "0 auto 8px" }} />
            <p style={{ color: "var(--error)", fontWeight: 600 }}>{error}</p>
          </div>
        )}

        {result && (
          <div className="animate-fade-in-up" data-testid="tracking-result">
            {/* Header card */}
            <div className="bento-item p-6 mb-4">
              <div className="flex items-center justify-between mb-5">
                <div>
                  <span className="overline" style={{ marginBottom: 4, display: "block" }}>Codigo de rastreio</span>
                  <span data-testid="tracking-result-code" style={{ fontFamily: "JetBrains Mono", fontSize: "1.4rem", fontWeight: 700, color: "var(--primary)" }}>{result.tracking_code}</span>
                </div>
                <span data-testid="tracking-result-status" style={{ padding: "6px 16px", borderRadius: 9999, fontSize: "0.8rem", fontWeight: 600, background: sc.bg, color: sc.color }}>
                  {STATUS_LABELS[result.status] || result.status}
                </span>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div style={{ padding: 12, borderRadius: 12, background: "var(--bg)" }}>
                  <span style={{ fontSize: "0.65rem", color: "var(--text-muted)", textTransform: "uppercase", fontWeight: 600, display: "block", marginBottom: 2 }}>Recolha</span>
                  <p style={{ fontWeight: 600, fontSize: "0.85rem" }}>{result.pickup_point}</p>
                </div>
                <div style={{ padding: 12, borderRadius: 12, background: "var(--bg)" }}>
                  <span style={{ fontSize: "0.65rem", color: "var(--text-muted)", textTransform: "uppercase", fontWeight: 600, display: "block", marginBottom: 2 }}>Entrega</span>
                  <p style={{ fontWeight: 600, fontSize: "0.85rem" }}>{result.delivery_point}</p>
                </div>
                <div style={{ padding: 12, borderRadius: 12, background: "var(--bg)" }}>
                  <span style={{ fontSize: "0.65rem", color: "var(--text-muted)", textTransform: "uppercase", fontWeight: 600, display: "block", marginBottom: 2 }}>Bolsa</span>
                  <div className="flex items-center gap-1">
                    <Bluetooth size={14} style={{ color: "var(--primary)" }} />
                    <span style={{ fontFamily: "JetBrains Mono", fontWeight: 600, fontSize: "0.85rem", color: "var(--primary)" }}>{result.bag_code}</span>
                  </div>
                </div>
                <div style={{ padding: 12, borderRadius: 12, background: "var(--bg)" }}>
                  <span style={{ fontSize: "0.65rem", color: "var(--text-muted)", textTransform: "uppercase", fontWeight: 600, display: "block", marginBottom: 2 }}>Entrega estimada</span>
                  <p style={{ fontWeight: 600, fontSize: "0.85rem" }}>
                    {result.estimated_delivery ? new Date(result.estimated_delivery).toLocaleString("pt-PT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "---"}
                  </p>
                </div>
              </div>
            </div>

            {/* Timeline */}
            <div className="bento-item p-6">
              <h3 style={{ fontFamily: "Outfit", fontWeight: 600, fontSize: "1rem", marginBottom: 16, display: "flex", alignItems: "center", gap: 8 }}>
                <Clock size={18} weight="duotone" style={{ color: "var(--primary)" }} /> Historico
              </h3><div className="flex flex-col gap-0">
                {result.timeline.map((step, i) => {
                  const isLast = i === result.timeline.length - 1;
                  const isDelivered = step.status === "delivered";
                  return (
                    <div key={i} className="flex items-start gap-4" style={{ position: "relative", paddingBottom: isLast ? 0 : 20 }}>
                      {!isLast && <div style={{ position: "absolute", left: 13, top: 28, bottom: 0, width: 2, background: "var(--border-color)" }} />}
                      <div style={{ width: 28, height: 28, borderRadius: "50%", background: isLast ? "var(--primary)" : isDelivered ? "var(--success)" : "var(--success)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, zIndex: 1 }}>
                        {isLast ? <div style={{ width: 10, height: 10, borderRadius: "50%", background: "white" }} /> : <CheckCircle size={16} weight="fill" color="white" />}
                      </div>
                      <div style={{ flex: 1 }}>
                        <p style={{ fontSize: "0.9rem", fontWeight: 600, marginBottom: 2 }}>{step.description}</p>
                        <div className="flex items-center gap-3" style={{ fontSize: "0.75rem", color: "var(--text-muted)" }}>
                          <span>{step.location}</span>
                          <span>{new Date(step.timestamp).toLocaleString("pt-PT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Chat sender ↔ receiver (Item 5) */}
            <div className="bento-item p-6" style={{ marginTop: 16 }}>
              <ShipmentChat trackingCode={result.tracking_code} />
            </div>

            {/* Live ETA card (only renders when status === in_transit) */}
            <RealtimeEtaCard trackingCode={result.tracking_code} status={result.status} variant="card" />
          </div>
        )}
      </div>
    </div>
  );
}
